import AddCircleIcon from '@mui/icons-material/AddCircle';
import PreviewIcon from '@mui/icons-material/Preview';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningIcon from '@mui/icons-material/Warning';

import React, { useEffect, useState } from 'react';

import { _zipCode, Company } from '_lib/api/apiTypes.shim';
import { WarningAlert } from '_lib/components/Alert/Alert';
import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { Button } from '_lib/components/Button/Button';
import { PrimaryCardHeader } from '_lib/components/CardHeader/CardHeader';
import { Checkbox } from '_lib/components/Checkbox/Checkbox';
import { Divider } from '_lib/components/Divider/Divider';
import { ErrorDisplay } from '_lib/components/ErrorDisplay/ErrorDisplay';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { FlexCard } from '_lib/components/FlexCard/FlexCard';
import { FormControlLabel } from '_lib/components/FormControlLabel/FormControlLabel';
import { IconButton } from '_lib/components/IconButton/IconButton';
import { ManualErrorBoundary } from '_lib/components/ManualErrorBoundary/ManualErrorBoundary';
import { Stack } from '_lib/components/Stack/Stack';
import { TextField } from '_lib/components/TextField/TextField';
import { Typography } from '_lib/components/Typography/Typography';

import { IconTypography } from '_lib/featureComponents/IconTypography/IconTypography';

import { useConfirmationDialog } from '_lib/hooks/ConfirmationDialogHook';

import { useReviewUpdatedZipCodesDialog } from './hooks/ReviewUpdatedZipCodesDialogHook';
import { ZipCodeData } from './hooks/useZipCodeData';
import { useZipCodeProcessor } from './hooks/useZipCodeProcessor';
import { ZipCodeItem, ZipCodeTable } from './ZipCodeTable';

export type ZipCodesProps = {
  company: Company;
  onDisableZipCodes: () => void;
  zipCodeData: ZipCodeData;
};

export const ZipCodes = (props: ZipCodesProps) => {
  const { company, onDisableZipCodes, zipCodeData } = props;
  const initialZipCodesServiced = company.callCenter?.zipCodesServiced || [];

  const confirmationDialog = useConfirmationDialog();
  const saveUpdatedZipCodesDialog = useReviewUpdatedZipCodesDialog();

  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const processedZipCodeData = useZipCodeProcessor({
    zones: zipCodeData.zones,
    businessUnits: zipCodeData.businessUnits,
    company
  });

  const [unassignedStZipsState, setUnassignedStZipsState] = useState<ZipCodeItem[]>([]);
  const [servicedZipsState, setServicedZipsState] = useState<ZipCodeItem[]>([]);

  useEffect(() => {
    setUnassignedStZipsState(processedZipCodeData.unassignedStZips);
  }, [processedZipCodeData.unassignedStZips]);

  useEffect(() => {
    setServicedZipsState(processedZipCodeData.servicedZips);
  }, [processedZipCodeData.servicedZips]);

  const handleSetUpdatedUnassignedStZipCodes = (selectedZipCodes: string[]) => {
    const updatedUnassignedStZipsState = unassignedStZipsState.map((zipCodeItem) => {
      return { ...zipCodeItem, selected: selectedZipCodes.includes(zipCodeItem.zip) };
    });

    setUnassignedStZipsState(updatedUnassignedStZipsState);
  };

  const handleSetUpdatedServicedZipCodes = (selectedZipCodes: string[]) => {
    const updatedServicedZipsState = servicedZipsState.map((zipCodeItem) => {
      return { ...zipCodeItem, selected: selectedZipCodes.includes(zipCodeItem.zip) };
    });

    setServicedZipsState(updatedServicedZipsState);
  };

  const [manuallyAddedZipError, setManuallyAddedZipError] = useState<string | null>(null);
  const [manuallyAddedZip, setManuallyAddedZip] = useState<string>('');

  const handleManuallyAddZipCode = (zip: string, setter: React.Dispatch<React.SetStateAction<ZipCodeItem[]>>) => {
    const result = _zipCode().safeParse(zip);
    if (!result.success) {
      setManuallyAddedZipError('Invalid zip code. Must be a valid US or CA zip code.');
      return;
    }

    if (unassignedStZipsState.find((item) => item.zip === zip)) {
      setManuallyAddedZipError('Duplicate zip code exists in "Unserviced Zip Codes"');
      return;
    }
    if (servicedZipsState.find((item) => item.zip === zip)) {
      setManuallyAddedZipError('Duplicate zip code exists in "Serviced Zip Codes"');
      return;
    }

    setter((prevState) => {
      return [...prevState, { zip, zone: null, businessUnitsItem: null, selected: true }];
    });
    setManuallyAddedZip('');
    setManuallyAddedZipError(null);
  };

  return (
    <FlexBox
      sx={(theme) => ({
        backgroundColor: theme.palette.background.default,
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        position: 'relative'
      })}>
      <BackdropSpinner open={zipCodeData.isFetching} />
      {!zipCodeData.isFetching && (
        <Stack padding={2} useFlexGap spacing={2} overflow='auto'>
          {!!validationErrors?.length && <ErrorDisplay errors={validationErrors} sx={{ borderRadius: 1 }} />}
          {zipCodeData.error && (
            <ManualErrorBoundary
              errorState={{
                message:
                  'Service Titan errors encountered while fetching zones and zip codes. It is recommended to resolve any issues before updating zip codes for the Relate company.'
              }}
              onRefresh={() => zipCodeData.refetch()}
              sx={{ height: 'auto' }}
            />
          )}
          {zipCodeData.zones && zipCodeData.zones.length === 0 && (
            <WarningAlert>
              <Typography text='The connected Service Titan tenant does not have any Zip Codes. If this is expected, you can continue managing zip codes for this company. If this is not expected, you may want to setup Zones & Zip Codes for the Service Titan tenant first.' />
            </WarningAlert>
          )}
          <FormControlLabel
            control={<Checkbox color='primary' checked={false} />}
            onClick={async () => {
              const result = await confirmationDialog.show({
                titleText: 'Enable Zones?',
                contentText:
                  'Enabling zones will remove all zip code associations from this company. Are you sure you wish to enable zones for this company?'
              });
              if (result?.action === 'CONFIRM') {
                onDisableZipCodes();
              }
            }}
            label={<Typography text='Enable Zones' />}
            sx={{ width: 'max-content' }}
          />

          {/* Invalid Zip Codes - invalid zip codes from ST that we do not allow saving to our company */}
          {processedZipCodeData.invalidZips.length > 0 && (
            <FlexCard sx={{ overflow: 'hidden', maxHeight: '750px', flexShrink: 0 }}>
              <PrimaryCardHeader
                title={<IconTypography variant='h3' startIcon={<WarningIcon />} text='Invalid Zip Codes' />}
              />
              <Typography
                text='The following zip codes from the Service Titan tenant are not supported. At this time only valid U.S. and Canadian zip codes are accepted.'
                sx={{ padding: 2 }}
              />
              <ZipCodeTable zipCodeItems={processedZipCodeData.invalidZips} readonlyMode />
            </FlexCard>
          )}
          {/* Duplicate Zip Codes - zips that occur in multiple ST zones */}
          {processedZipCodeData.duplicateZips.length > 0 && (
            <FlexCard sx={{ overflow: 'hidden', maxHeight: '750px', flexShrink: 0 }}>
              <PrimaryCardHeader
                title={<IconTypography variant='h3' startIcon={<WarningIcon />} text='Duplicate Zip Codes' />}
              />
              <Typography
                text='These were configured more than once for the Service Titan tenant. Only one occurrence of the zip code will exist in the management lists below.'
                sx={{ padding: 2 }}
              />
              <ZipCodeTable zipCodeItems={processedZipCodeData.duplicateZips} readonlyMode />
            </FlexCard>
          )}
          <FlexCard sx={{ overflow: 'hidden', maxHeight: '750px', flexShrink: 0 }}>
            {unassignedStZipsState.length > 0 && servicedZipsState.length > 0 && (
              <>
                <PrimaryCardHeader title='Unserviced Zip Codes' />
                <Typography
                  text='These only exist in the Service Titan tenant and are currently not serviced by the call center. Select any that you wish to add to service.'
                  sx={{ padding: 2 }}
                />
                <ZipCodeTable
                  zipCodeItems={unassignedStZipsState}
                  setSelectedZipCodes={handleSetUpdatedUnassignedStZipCodes}
                />
              </>
            )}
            {servicedZipsState.length === 0 && (
              <>
                <PrimaryCardHeader title='Serviced Zip Codes' />
                {unassignedStZipsState.length > 0 && (
                  <Typography
                    text='The zip codes below are from the connected the Service Titan tenant. Select all zips codes that should be serviced for this Relate CX company. If there are other zip codes that should be serviced you can add them manually.'
                    sx={{ padding: 2 }}
                  />
                )}
                {unassignedStZipsState.length === 0 && (
                  <WarningAlert sx={{ padding: 2 }}>
                    No Zip codes were found from Service Titan tenant. If you plan to use zip codes from the Service
                    Titan tenant, please make sure the tenant has Zones and Zip Codes configured. Otherwise, if you do
                    not plan to use zip codes from the Service Titan tenant, you can add them to the Relate company
                    below.
                  </WarningAlert>
                )}

                <Stack useFlexGap direction='row' sx={{ alignItems: 'flex-start', marginLeft: 2, marginBottom: 1 }}>
                  <TextField
                    value={manuallyAddedZip}
                    size='small'
                    onChange={(e) => {
                      setManuallyAddedZip(e.target.value);
                      setManuallyAddedZipError(null);
                    }}
                    onEnter={() => {
                      handleManuallyAddZipCode(manuallyAddedZip, setUnassignedStZipsState);
                    }}
                    color={manuallyAddedZipError ? 'error' : undefined}
                    label='Add zip code to Company'
                    helperText={manuallyAddedZipError}
                    sx={{ minWidth: '250px' }}
                  />
                  <IconButton
                    size='small'
                    onClick={() => {
                      handleManuallyAddZipCode(manuallyAddedZip, setUnassignedStZipsState);
                    }}
                    color={manuallyAddedZipError ? 'error' : 'primary'}
                    sx={{ marginTop: 1.5 }}>
                    <AddCircleIcon />
                  </IconButton>
                </Stack>
                <ZipCodeTable
                  zipCodeItems={unassignedStZipsState}
                  setSelectedZipCodes={handleSetUpdatedUnassignedStZipCodes}
                />
              </>
            )}
            {unassignedStZipsState.length === 0 && servicedZipsState.length > 0 && (
              <>
                <PrimaryCardHeader title='Unserviced Zip Codes' />
                <Typography text='All zip codes from the Service Titan tenant are serviced.' sx={{ padding: 2 }} />
              </>
            )}
          </FlexCard>

          {servicedZipsState.length > 0 && (
            <FlexCard sx={{ overflow: 'hidden', maxHeight: '750px', flexShrink: 0 }}>
              <PrimaryCardHeader title='Serviced Zip Codes' />
              <Typography
                text='These are currently serviced by the call center. Unselect any that you wish to remove from service.'
                sx={{ padding: 2 }}
              />
              <Stack useFlexGap direction='row' sx={{ alignItems: 'flex-start', marginLeft: 2, marginBottom: 1 }}>
                <TextField
                  value={manuallyAddedZip}
                  size='small'
                  onChange={(e) => {
                    setManuallyAddedZip(e.target.value);
                    setManuallyAddedZipError(null);
                  }}
                  onEnter={() => {
                    handleManuallyAddZipCode(manuallyAddedZip, setServicedZipsState);
                  }}
                  color={manuallyAddedZipError ? 'error' : 'primary'}
                  label='Add zip code to Company'
                  helperText={manuallyAddedZipError}
                  sx={{ minWidth: '250px' }}
                />
                <IconButton
                  size='small'
                  color={manuallyAddedZipError ? 'error' : 'primary'}
                  onClick={() => {
                    handleManuallyAddZipCode(manuallyAddedZip, setServicedZipsState);
                  }}
                  sx={{ marginTop: 1.5 }}>
                  <AddCircleIcon />
                </IconButton>
              </Stack>
              <ZipCodeTable
                zipCodeItems={servicedZipsState}
                setSelectedZipCodes={handleSetUpdatedServicedZipCodes}
                showSyncStatusColumn
              />
            </FlexCard>
          )}
        </Stack>
      )}
      <FlexBox sx={{ marginTop: 'auto', width: '100%', flexDirection: 'column' }}>
        <Divider />
        <FlexBox
          sx={(theme) => ({
            backgroundColor: theme.palette.background.paper,
            flexDirection: 'row',
            padding: 2
          })}>
          <Button
            onClick={async () => {
              const result = await confirmationDialog.show({
                titleText: 'Refresh All?',
                contentText:
                  'This will reset any unsaved selections and pull the latest zip codes from Service Titan. Are you sure you wish to refresh?'
              });
              if (result?.action !== 'CONFIRM') return;
              setValidationErrors([]);
              zipCodeData.refetch();
            }}
            startIcon={<RefreshIcon />}
            text='Refresh All'
            variant='contained'
            color='primaryInverted'
            sx={{ textTransform: 'none' }}
          />
          <Button
            onClick={async () => {
              setValidationErrors([]);
              const result = await saveUpdatedZipCodesDialog.show({
                companyId: company.id,
                initialZipCodesServiced,
                selectedUnassignedStZips: unassignedStZipsState
                  .filter((zipItem) => zipItem.selected)
                  .map((zipItem) => zipItem.zip),
                selectedServicedZips: servicedZipsState
                  .filter((zipItem) => zipItem.selected)
                  .map((zipItem) => zipItem.zip)
              });
              if (result?.action === 'CONFIRM' && result.value?.updateErrors?.length) {
                setValidationErrors(result.value.updateErrors);
              }
            }}
            startIcon={<PreviewIcon />}
            text='Review & Save'
            variant='contained'
            sx={{ textTransform: 'none', marginLeft: 'auto' }}
          />
        </FlexBox>
      </FlexBox>
      {confirmationDialog.element}
      {saveUpdatedZipCodesDialog.element}
    </FlexBox>
  );
};
